import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useConceptData, useHotkeys, todayItems, STATUS_LABEL, rel } from "@/concepts/shared";
import { D, Shell, Palette, Tag, Kbd, Section, to } from "@/concepts/clinical/kit";

const BUCKETS = [
  ["overdue", "Overdue follow-ups", [0], "bg-[color:var(--fail-soft)]", "text-[color:var(--fail)]"],
  ["due", "Due today", [1], "bg-[color:var(--surface)]", ""],
  ["review", "Awaiting review", [2, 3], "bg-[color:var(--signal)]", "text-[color:var(--signal-ink)]"],
  ["handoff", "Approaching handoff", [4], "bg-[color:var(--accent-soft)]", "text-[color:var(--accent-text)]"],
];
const KIND_TONE = { 0: "fail", 1: "unk", 2: "neutral", 3: "met", 4: "accent" };
const PIPE = [["ready_for_first_visit", "var(--pipe-5)"], ["pre_screening", "var(--pipe-4)"], ["contacted", "var(--pipe-3)"], ["pending_review", "var(--pipe-2)"], ["queued", "var(--pipe-1)"]];

const Ribbon = ({ counts, total, bucket, setBucket }) => (
  <div className="flex h-[148px] gap-[3px]" role="group" aria-label="Workload by type — select to filter the queue" data-testid="cm-workload">
    {BUCKETS.map(([k, l, , bg, fg]) => (
      <button key={k} onClick={() => setBucket(bucket === k ? "all" : k)} aria-pressed={bucket === k} data-testid={`cm-workload-${k}`}
        style={{ flexGrow: Math.max(counts[k], total * 0.14), flexBasis: 0 }}
        className={`flex min-w-0 flex-col justify-between rounded-[6px] p-4 text-left transition-[box-shadow] ${bg} ${bucket === k ? "shadow-[inset_0_0_0_2px_var(--ink)]" : "hover:shadow-[inset_0_0_0_1px_var(--rule-strong)]"}`}>
        <span className={`display tnum text-[52px] ${fg}`}>{counts[k]}</span>
        <span className={`text-[13px] font-medium leading-tight ${fg || "text-[color:var(--ink-2)]"}`}>{l}</span>
      </button>
    ))}
  </div>
);

const NextUp = ({ x }) => (
  <Link to={x.to} className="cm-in mt-10 grid grid-cols-[auto_1fr_auto] items-center gap-8 rounded-[8px] border rule-strong px-6 py-5 hover:border-[color:var(--ink)]" data-testid="cm-next-up">
    <span className="flex flex-col gap-2"><span className="eyebrow">Next up</span><Tag tone={KIND_TONE[x.urgency]}>{x.kind}</Tag></span>
    <span className="min-w-0 border-l rule pl-8"><span className="block truncate text-[22px] font-semibold tracking-[-0.025em]">{x.title}</span>
      <span className="text-[14px] text-[color:var(--mute)]">{x.who} <span className="mono">{x.ref}</span> · {x.study} · {x.when}</span></span>
    <span className="btn btn-signal !px-5 !py-3 !text-[15px]">Open {x.who.split(" ")[0]}<Kbd>↵</Kbd></span>
  </Link>
);

const QueueRow = ({ x, i, on, hover }) => (
  <Link to={x.to} onMouseEnter={hover} data-testid={`cm-queue-${i}`} aria-current={on || undefined}
    className={`grid grid-cols-[34px_168px_minmax(0,1fr)_104px_128px_96px] items-center gap-4 border-b rule px-3 py-3 ${on ? "row-sel" : "row-hov"}`}>
    <span className="mono tnum text-[color:var(--mute)]">{String(i + 1).padStart(2, "0")}</span>
    <span><Tag tone={KIND_TONE[x.urgency]}>{x.kind}</Tag></span>
    <span className="min-w-0"><span className="block truncate text-[15px] font-medium">{x.title}</span><span className="text-[13px] text-[color:var(--mute)]">{x.who} · <span className="mono">{x.ref}</span></span></span>
    <span className="mono text-[color:var(--ink-2)]">{x.study}</span>
    <span className={`whitespace-nowrap text-[13px] ${x.urgency === 0 ? "font-medium text-[color:var(--fail)]" : "text-[color:var(--mute)]"}`}>{x.when}</span>
    <span className={`flex items-center justify-end gap-1 text-[13px] font-medium ${on ? "text-[color:var(--accent-text)]" : "text-[color:var(--ink-2)]"}`} title={`Opens the workbench to: ${x.action}`}>Open<ArrowRight size={14} aria-hidden /></span>
  </Link>
);

const StudyProgress = ({ s }) => (
  <Link to={to("workbench", `?study=${s.id}`)} className="block border-b rule py-3.5 hover:bg-[color:var(--surface)]" data-testid={`cm-study-${s.reference}`}>
    <div className="flex items-baseline justify-between gap-3">
      <span className="min-w-0"><span className="mono text-[color:var(--mute)]">{s.reference}</span><span className="block truncate text-[14px] font-medium">{s.name}</span></span>
      <span className="whitespace-nowrap text-[22px] font-semibold tracking-[-0.03em] tnum">{s.pipeline.ready_for_first_visit}<span className="text-[14px] font-medium text-[color:var(--mute)]"> / {s.target}</span></span>
    </div>
    <div className="mt-2 flex h-[8px] gap-[2px] overflow-hidden rounded-[2px] bg-[color:var(--surface-2)]" role="img"
      aria-label={PIPE.map(([k]) => `${STATUS_LABEL[k]} ${s.pipeline[k]}`).join(", ") + ` of target ${s.target}`}>
      {PIPE.map(([k, c]) => s.pipeline[k] > 0 && <span key={k} style={{ width: `${(s.pipeline[k] / s.target) * 100}%`, background: c }} />)}
    </div>
  </Link>
);

export const ClinicalToday = () => {
  const { dash, studies, studyId } = useConceptData();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [bucket, setBucket] = useState("all");
  const [cur, setCur] = useState(0);
  const [all, setAll] = useState(false);
  const items = todayItems(dash, D);
  const urg = bucket === "all" ? null : BUCKETS.find((b) => b[0] === bucket)[2];
  const filtered = items.filter((x) => !urg || urg.includes(x.urgency));
  const shown = all ? filtered : filtered.slice(0, 12);
  useHotkeys({ "mod+k": () => setOpen(true), j: () => setCur((i) => Math.min(shown.length - 1, i + 1)), k: () => setCur((i) => Math.max(0, i - 1)), enter: () => shown[cur] && nav(shown[cur].to) });
  if (!dash) return <Shell active="today" onSearch={() => setOpen(true)}><p className="p-10 text-[color:var(--mute)]">Loading…</p></Shell>;
  const counts = Object.fromEntries(BUCKETS.map(([k, , u]) => [k, items.filter((x) => u.includes(x.urgency)).length]));
  const d = new Date(dash.today);
  const hour = new Date().getHours();
  return (
    <Shell active="today" onSearch={() => setOpen(true)}>
      <main className="mx-auto max-w-[1440px] px-10 pb-20 pt-10">
        <div className="grid grid-cols-12 items-end gap-10">
          <div className="col-span-12 xl:col-span-6">
            <p className="eyebrow">{d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · Clinical research · QASMC</p>
            <h1 className="display mt-4 text-[54px]" data-testid="cm-today-heading">Good {hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening"}.<br />
              <span className="tnum">{items.length}</span> things need you today.</h1>
            <p className="mt-5 max-w-[560px] text-[16px] leading-relaxed text-[color:var(--ink-2)]">
              {counts.overdue} follow-ups are overdue. {counts.review} candidates are waiting for review and {counts.handoff} are close to handoff, across {dash.metrics.recruiting_studies} recruiting studies.
            </p>
          </div>
          <div className="col-span-12 xl:col-span-6"><Ribbon counts={counts} total={items.length} bucket={bucket} setBucket={(b) => { setBucket(b); setCur(0); }} /></div>
        </div>
        {items[0] && <NextUp x={items[0]} />}
        <div className="mt-12 grid grid-cols-12 gap-10">
          <Section className="col-span-12 xl:col-span-8" testId="cm-queue" title="Priority queue"
            meta={<span className="flex items-center gap-3">{bucket !== "all" && <button className="font-medium text-[color:var(--accent-text)]" onClick={() => setBucket("all")} data-testid="cm-clear-bucket">Clear filter</button>}<span>{filtered.length} items · <Kbd>J</Kbd> <Kbd>K</Kbd> move · <Kbd>↵</Kbd> open</span></span>}>
            {shown.map((x, i) => <QueueRow key={x.id} x={x} i={i} on={cur === i} hover={() => setCur(i)} />)}
            {filtered.length > 12 && <button onClick={() => setAll(!all)} className="btn btn-line mt-4" data-testid="cm-queue-more">{all ? "Show fewer" : `Show all ${filtered.length}`}</button>}
          </Section>
          <aside className="col-span-12 space-y-10 xl:col-span-4">
            <Section title="Study progress" meta="Ready for first visit / target" testId="cm-progress">
              {dash.study_progress.map((s) => <StudyProgress key={s.id} s={s} />)}
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">{PIPE.map(([k, c]) => <span key={k} className="flex items-center gap-1.5 text-[12px] text-[color:var(--mute)]"><span className="h-2 w-2 rounded-[1px]" style={{ background: c }} />{STATUS_LABEL[k]}</span>)}</div>
            </Section>
            <Section title="Recent activity" testId="cm-activity">
              {dash.recent_activity.slice(0, 5).map((a) => <p key={a.id} className="border-b rule py-2.5 text-[13.5px] leading-snug">{a.summary}<span className="block text-[12px] text-[color:var(--mute)]">{rel(a.at)} · {a.actor_name}</span></p>)}
            </Section>
          </aside>
        </div>
      </main>
      <Palette open={open} setOpen={setOpen} studies={studies || []} studyId={studyId} />
    </Shell>
  );
};
